import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import uuid from "react-uuid";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import useConfirm from "hooks/useConfirm";
import { GridView, SvgComponent } from "components";
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { TransaccionesRecurrentesManager } from "managers";
import { DateHelper, DEFAULT_PAGINATION_VALUES } from "utils";
import { DateTimePicker } from "components/DateTimePicker/DateTimePicker";
import {
  eCF,
  TIPO_COMPROBANTE_NOTA_DEBITO,
  TIPO_COMPROBANTE_NOTA_CREDITO,
  MONEDA_PESO_DOMINICANO_ISO,
} from "models/ECF/ECF";

// ─── Constantes de filtro ────────────────────────────────────────────────────
const TIPOS_COMPROBANTES_RECURRENTES = [
  { value: "", label: "Todos" },
  ...eCF.TIPOS_COMPROBANTES.filter(
    (t) =>
      t.value !== TIPO_COMPROBANTE_NOTA_DEBITO &&
      t.value !== TIPO_COMPROBANTE_NOTA_CREDITO
  ),
];

const ESTATUS_LIST = [
  { value: "", label: "Todos" },
  { value: "A", label: "Activa" },
  { value: "C", label: "Cancelada" },
];

const PROGRAMACION_LIST = [
  { value: "", label: "Todos" },
  { value: "S", label: "Semanal" },
  { value: "Q", label: "Quincenal" },
  { value: "M", label: "Mensual" },
  { value: "T", label: "Trimestral" },
  { value: "6", label: "Semestral" },
  { value: "A", label: "Anual" },
  { value: "O", label: "Otro" },
];

// Helpers para mostrar labels en la grilla
function getProgramacionLabel(value) {
  const item = PROGRAMACION_LIST.find((p) => p.value === value);
  return item ? item.label : value;
}

function getEstatusLabel(value) {
  const item = ESTATUS_LIST.find((e) => e.value === value);
  return item ? item.label : value;
}

function getTipoeCFLabel(value) {
  const item = TIPOS_COMPROBANTES_RECURRENTES.find((t) => t.value === value);
  return item ? item.label : value;
}

function getSimboloMoneda(tipoMoneda) {
  const simbolos = {
    USD: "US$",
    EUR: "€",
    GBP: "£",
    MXN: "MX$",
    COP: "COL$",
    BRL: "R$",
    CAD: "CA$",
    CHF: "CHF",
    CNY: "¥",
    JPY: "¥",
    DKK: "kr",
    NOK: "kr",
    SEK: "kr",
    VEF: "Bs.F",
    HTG: "G",
  };
  return simbolos[tipoMoneda] || tipoMoneda;
}

// ─── Componente principal ────────────────────────────────────────────────────
export default function TransaccionesRecurrentesList({
  showLoading,
  hideLoading,
}) {
  const history = useHistory();
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const [fechaCreacionFilterkey, setFechaCreacionFilterKey] = useState(
    uuid()
  );

  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const { Message, Warning, cerrarAlertas } = useMessage();
  const { Confirm } = useConfirm();

  useEffect(() => {
    buscar();
    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where]);

  function handleCancelarClick(row) {
    if (row.Estatus === "C") {
      Warning({ message: "Esta transacción recurrente ya se encuentra cancelada." });
      return;
    }
    Confirm({
      title: "Cancelar Transacción Recurrente",
      textBody: `¿Está seguro que desea cancelar la transacción recurrente #${row.IdSec}?`,
      onConfirm: () => cancelarTransaccion(row.IdSec),
      onCancel: () => {},
    });
  }

  function cancelarTransaccion(idSec) {
    showLoading();
    TransaccionesRecurrentesManager.cancelar(idSec)
      .then(() => {
        Message({ message: "Transacción recurrente cancelada exitosamente." });
        setWhere({ ...where });
      })
      .catch((err) => handleException(err))
      .finally(() => hideLoading());
  }

  function buscar() {
    const whereWithPaginationValues = {
      ...where,
      ...pagination,
      FechaInicioCreacion: where.RangoFechasCreacion?.fechaInicio,
      FechaFinCreacion: where.RangoFechasCreacion?.fechaFin,
    };

    TransaccionesRecurrentesManager.listado(whereWithPaginationValues)
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        const data = (res.Data ?? []).map((item) => ({
          ...item,
          TipoeCFLabel: getTipoeCFLabel(item.TipoeCF),
          ProgramacionLabel: getProgramacionLabel(item.Programacion),
          EstatusLabel: getEstatusLabel(item.Estatus),
          Documento: item.RNCComprador || item.IdentificadorExtranjero || "",
          FechaCreacionFormatted: DateHelper.formatStringDate(
            item.FechaCreacion,
            true
          ),
        }));
        setList(data);
      })
      .catch((error) => {
        handleException(error);
      })
      .finally(() => {
        hideLoading();
      });
  }

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prev) => ({
      ...prev,
      IndiceDePagina: pageNumber - 1,
    }));
    setWhere({ ...where });
  };

  const handleEjecutarProceso = () => {
    Confirm({
      title: "Procesar todas las Transacciones pendientes a generar este mes",
      textBody: (<>Se procesarán todas las transacciones recurrentes que están pendientes de generarse en este mes.<br/><br/>¿Desea continuar de todos modos?</>),
      onConfirm: () => {
        showLoading();
        TransaccionesRecurrentesManager.ejecutarProceso()
          .then(() => {
            Message({ message: "Proceso programado exitosamente. Se ejecutará en breve." });
            setWhere({ ...where });
          })
          .catch((err) => handleException(err))
          .finally(() => hideLoading());
      },
      onCancel: () => {},
    });
  };

  const handleCrearNuevo = () => {
    history.push("/TransaccionesRecurrentesEdit");
  };

  const handleVerClick = (row) => {
    history.push(`/TransaccionesRecurrentesEdit?Id=${row.IdSec}`);
  };

  return (
    <>
      <div style={{ paddingRight: "6px" }}>
        <ListHeader
          titulo="Transacciones Recurrentes"
          crearNuevo={handleCrearNuevo}
          emitirecfs={handleEjecutarProceso}
        />
      </div>

      <div style={{ paddingRight: "20px" }}>
      <GridView
        pagination={pagination}
        setPagination={setPagination}
        allowPaging={pagination.EsPaginable}
        pageSize={pagination.CantidadPorPagina}
        pageIndex={pagination.IndiceDePagina}
        lastPageNumber={pagination.PaginasTotal}
        dataSource={list}
        onPageIndexChanging={onPageIndexChanging}
        where={where}
        setWhere={setWhere}
        onReset={() => {
          setFechaCreacionFilterKey(uuid());
        }}
      >
        <GridView.Column
          isField
          width="140px"
          showClearFilter={true}
          bodyRenderer={(row) => {
            return (
              <div className="float-right">
                <IconButton onClick={() => handleVerClick(row)}>
                  <ToolTip message="Editar">
                    <div>
                      <SvgComponent
                        iconName="pencil"
                        height="25"
                        width="25"
                      />
                    </div>
                  </ToolTip>
                </IconButton>
                {row.Estatus !== "C" && (
                  <IconButton onClick={() => handleCancelarClick(row)}>
                    <ToolTip message="Cancelar">
                      <div>
                        <SvgComponent
                          iconName="cancel"
                          height="25"
                          width="25"
                        />
                      </div>
                    </ToolTip>
                  </IconButton>
                )}
              </div>
            );
          }}
        />

        <GridView.Column
          headerText="Id"
          dataField="IdSec"
          sortExpression="IdSec"
          width="110px"
        />

        <GridView.Column
          headerText="Tipo Comprobante"
          dataField="TipoeCFLabel"
          sortExpression="TipoeCF"
          width="180px"
          filterRenderer={() => (
            <Select
              className="react-select primary"
              classNamePrefix="react-select"
              options={TIPOS_COMPROBANTES_RECURRENTES}
              value={TIPOS_COMPROBANTES_RECURRENTES.filter(
                (o) =>
                  o.value ===
                  (where.TipoeCF ?? TIPOS_COMPROBANTES_RECURRENTES[0].value)
              )}
              name="TipoeCF"
              onChange={(e) =>
                setWhere({ ...where, TipoeCF: e.value })
              }
            />
          )}
        />

        <GridView.Column
          headerText="Documento"
          dataField="Documento"
          sortExpression="RNCComprador"
          width="180px"
        />

        <GridView.Column
          headerText="Razón Social"
          dataField="RazonSocialComprador"
          sortExpression="RazonSocialComprador"
          width="250px"
        />

        

        <GridView.Column
          headerText="Programación"
          dataField="ProgramacionLabel"
          sortExpression="Programacion"
          width="175px"
          filterRenderer={() => (
            <Select
              className="react-select primary"
              classNamePrefix="react-select"
              options={PROGRAMACION_LIST}
              value={PROGRAMACION_LIST.filter(
                (o) =>
                  o.value ===
                  (where.Programacion ?? PROGRAMACION_LIST[0].value)
              )}
              name="Programacion"
              onChange={(e) =>
                setWhere({ ...where, Programacion: e.value })
              }
            />
          )}
        />

        <GridView.Column
          headerText="Estatus"
          dataField="EstatusLabel"
          sortExpression="Estatus"
          width="150px"
          filterRenderer={() => (
            <Select
              className="react-select primary"
              classNamePrefix="react-select"
              options={ESTATUS_LIST}
              value={ESTATUS_LIST.filter(
                (o) =>
                  o.value ===
                  (where.Estatus ?? ESTATUS_LIST[0].value)
              )}
              name="Estatus"
              onChange={(e) =>
                setWhere({ ...where, Estatus: e.value })
              }
            />
          )}
        />
        <GridView.Column
          headerText="Monto Total"
          dataField="MontoTotal"
          sortExpression="MontoTotal"
          width="150px"
          number={true}
          bodyRenderer={(row) => {
            const formatted = Intl.NumberFormat("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(row.MontoTotal);
            return (
              <span style={{ float: "right" }}>
                {row.TipoMoneda === "USD" ? "US$ " : ""}{formatted}
              </span>
            );
          }}
        />
      </GridView>
      </div>
    </>
  );
}
